import React from 'react'
import SearchPage from 'containers/SearchPage'
import { Table, Tag, Select, Button } from '@arnsbogroup/react-voicearchive'
import { FormattedDate, FormattedTime, useIntl } from 'react-intl'
import classNames from 'classnames'
import messages from './messages'

const Span = ({ children }) => <span>{ children }</span>

const Deadline = ({ deadline }) => {
    const value = new Date(deadline * 1000)
    return (
      <div className="space-x-1">
        <span>
          <FormattedDate value={value} />
        </span>
        <span>
          <FormattedTime value={value} />
        </span>
      </div>
    )
  }

const AccountingMergeInvoicesPage = ( { history, ...props } ) => {
    const { formatMessage } = useIntl()
    const [rowsSelected, setRowsSelected] = React.useState([]);
    const [mainOrder, setMainOrder] = React.useState(null);
    const [state, setState] = React.useState({
      isLoading: false
    });

    const mergeAndInvoiceOrders = () => {
      const ordersToMerge = rowsSelected.filter((uid) => uid !== mainOrder);

      setState({
        isLoading: true
      });
      const loading = toastr.info(
        'Please wait. Do not reload or navigate away from this page!',
        'Merging and Invoicing..',
        {
          timeOut: 0,
          extendedTimeOut: 0,
        }
      )

      fetch(`/rest/v2/accounting/merge-and-invoice-orders`, {
        method: 'POST',
        body: JSON.stringify({
          orderIdList: ordersToMerge,
          mainOrderId: mainOrder,
        }),
      })
        .then(response => response.json())
        .then((response) => {
          setState({
            isLoading: false
          });
          loading.remove();
          if (response.errors && response.errors.length > 0) {
            toastr.error('An error occured while merging orders:', response.errors.join(', '))
          } else {
            if (response.meta.emptyInvoice) {
              toastr.info('No tasks was eligable for invoicing. Skipping sync..')
            } else {
              toastr.success('Orders merged and invoiced, on order #' + mainOrder)
              setRowsSelected([]);
              setMainOrder(null);
            }
          }
        })
        .catch((error) => {
          toastr.error(error)
        });
    };

    const MergeAndInvoiceOrders = () => {
      return <div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <h3>{formatMessage(messages.selectedOrderHeader)}</h3>
            {rowsSelected.map((uid) => {
              return (uid !== mainOrder) ? <Tag key={uid}>{uid}</Tag> : null;
            })}
          </div>

          <div>
            <Select
              label={formatMessage(messages.mainOrderSelectLabel)}
              size="xs"
              value={{ value: mainOrder, label: mainOrder }}
              options={ rowsSelected.map((uid) => ({ value: uid, label: uid })) }
              onChange={(selected) => setMainOrder(selected.value)}
              isDisabled={state.isLoading}
            />

            <Button
              onClick={() => mergeAndInvoiceOrders()}
              size="xs"
              className="mt-2"
              disabled={state.isLoading || !mainOrder || rowsSelected.length < 2}
            >
              {formatMessage(messages.mergeInvoiceOrdersButton)}
            </Button>
          </div>
        </div>
      </div>
    }

    const invoiceSingleOrder = () => {
      setState({
        isLoading: true
      });
      const loading = toastr.info(
        'Please wait. Do not reload or navigate away from this page!',
        'Invoicing..',
        {
          timeOut: 0,
          extendedTimeOut: 0,
        }
      )

      fetch(`/rest/v2/accounting/invoice-single-order`, {
        method: 'POST',
        body: JSON.stringify({
          orderId: rowsSelected[0],
        }),
      })
        .then(response => response.json())
        .then((response) => {
          setState({
            isLoading: false
          });
          loading.remove();
          if (response.errors && response.errors.length > 0) {
            toastr.error('An error occured while invoicing order:', response.errors.join(', '))
          } else {
            if (response.meta.emptyInvoice) {
              toastr.info('Task was not eligable for invoicing. Skipping sync..')
            } else {
              toastr.success('Order invoiced #' + rowsSelected[0])
              setRowsSelected([]);
              setMainOrder(null);
            }
          }
        })
        .catch((error) => {
          toastr.error(error)
        });
    }

    const InvoiceSingleOrder = () => {
      return <div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <h3>{formatMessage(messages.invoiceSingleOrderHeader)}</h3>
            {rowsSelected.map((uid) => {
              return (uid !== mainOrder) ? <Tag key={uid}>{uid}</Tag> : null;
            })}
          </div>

          <div>
            <Button
              onClick={() => invoiceSingleOrder()}
              size="xs"
              className="mt-2"
              disabled={state.isLoading || rowsSelected.length != 1}
            >
              {formatMessage(messages.invoiceSingleOrderButton)}
            </Button>
          </div>
        </div>
      </div>
    }

    return(
      <div className="w-full flex flex-col">
        <SearchPage {...props}
            baseApiUrl="/rest/v2/accounting"
            rowSelectionKey="uid"
            onRowSelected={(uid) => {
              if (rowsSelected.includes(uid)) {
                setRowsSelected(rowsSelected.filter((selectedUid) => selectedUid !== uid));
              } else {
                setRowsSelected([...rowsSelected, uid]);
              }
            }}
            rowsSelected={rowsSelected}
            components={{
              Tr: ({ childProps, ...innerProps }) => (
                <Table.Tr
                  {...innerProps }
                  className={classNames({
                    'cursor-pointer': !!childProps,
                    'bg-blue-50': props?.location?.pathname.startsWith(`/orders/${childProps?.uid}`),
                  })}
                  onClick={childProps?.uid ? () => history.push(`/orders/${childProps.uid}`) : undefined}
                />
              )
            }}
            head={{
                uid: { sortable: true, label: '#' },
                name: { sortable: true, label: 'Name' },
                customerName: { sortable: true, label: 'Company' },
                coordinatorName: { sortable: true, label: 'Coordinator' },
                suppliers: {label: 'Suppliers' },
                deadline: { sortable: true, label: 'Deadline' },
            }}
            body={{
                uid: Span,
                name: Span,
                customerName: Span,
                coordinatorName: Span,
                suppliers: Span,
                deadline: Deadline,
            }}
        />

        {(rowsSelected.length === 1) && (
          <div className='bg-gray-100 py-8 px-4 grow'>
            <InvoiceSingleOrder />
          </div>
        )}

        {(rowsSelected.length > 1) && (
          <div className='bg-gray-100 py-8 px-4 grow'>
            <MergeAndInvoiceOrders />
          </div>
        )}

      </div>
    )
}

export default AccountingMergeInvoicesPage
